/**
 * LoginPage — CR-0030 (v4 — fix dual icon, standard padding, SVG logo).
 *
 * Password field uses type="text" with CSS -webkit-text-security: disc
 * to prevent the browser from injecting its native password reveal icon.
 * Only our custom Lucide Eye/EyeOff icon is rendered.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, User, Lock } from "lucide-react";
import useAuthStore from "@/store/useAuthStore";
import { loginApi } from "@/api/auth";

export default function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password.trim()) {
      setError("Please enter both email and password.");
      return;
    }
    setLoading(true);
    try {
      const data = await loginApi(email, password);
      const token =
        data?.data?.token ||
        data?.token ||
        data?.access_token ||
        data?.data?.access_token ||
        null;
      const user = data?.data?.user || data?.user || { email };
      if (!token) {
        setError("Login failed — no token received.");
        return;
      }
      setAuth(token, user, remember);
      navigate("/", { replace: true });
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.errors?.[0]?.message ||
        err?.response?.data?.detail?.message ||
        err?.response?.data?.detail?.detail ||
        (typeof err?.response?.data?.detail === "string"
          ? err.response.data.detail
          : null) ||
        err?.response?.data?.message ||
        "Invalid email or password.";
      setError(typeof detail === "string" ? detail : "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  }

  const fieldStyle = {
    borderColor: "#CCCCCC",
    backgroundColor: "#FFFFFF",
    color: "#1F2937",
  };

  const iconColor = "#666666";

  return (
    <div
      data-testid="login-page"
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: "#FFFFFF" }}
    >
      <style>{`
        .login-field:focus {
          outline: none !important;
          box-shadow: none !important;
          border-color: #CCCCCC !important;
        }
        .pwd-mask {
          -webkit-text-security: disc;
          -moz-text-security: disc;
          text-security: disc;
        }
      `}</style>

      <form
        data-testid="login-form"
        onSubmit={handleSubmit}
        className="w-full max-w-[380px] rounded-md shadow-xl px-6 py-8 sm:px-8 sm:py-10 flex flex-col items-center gap-5"
        style={{ backgroundColor: "#FFFFFF", border: "1px solid #E5E7EB" }}
      >
        {/* SVG Logo */}
        <img
          data-testid="login-logo"
          src="/mygenie_logo.svg"
          alt="MyGenie logo"
          className="w-28 h-auto sm:w-32 object-contain"
        />

        {/* Tagline */}
        <div className="text-center space-y-0.5">
          <p
            data-testid="login-tagline-line1"
            className="text-base sm:text-lg font-bold"
            style={{ color: "#F26B33" }}
          >
            Streamlined Hospitality.
          </p>
          <p
            data-testid="login-tagline-line2"
            className="text-base sm:text-lg font-bold"
            style={{ color: "#329937" }}
          >
            Exceptional Experience.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div
            data-testid="login-error"
            className="w-full text-center text-sm py-2 px-3 rounded-md"
            style={{ backgroundColor: "#FEE2E2", color: "#DC2626" }}
          >
            {error}
          </div>
        )}

        {/* Email */}
        <div className="w-full relative">
          <User
            size={20}
            className="absolute left-4 top-1/2 -translate-y-1/2"
            style={{ color: iconColor }}
          />
          <input
            data-testid="login-email-input"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            className="login-field w-full pl-12 pr-4 py-2.5 rounded-lg border text-sm outline-none"
            style={fieldStyle}
          />
        </div>

        {/* Password — uses type="text" + CSS masking to avoid browser native eye icon */}
        <div className="w-full relative">
          <Lock
            size={20}
            className="absolute left-4 top-1/2 -translate-y-1/2"
            style={{ color: iconColor }}
          />
          <input
            data-testid="login-password-input"
            type="text"
            inputMode="text"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="off"
            className={`login-field w-full pl-12 pr-12 py-2.5 rounded-lg border text-sm outline-none ${showPassword ? "" : "pwd-mask"}`}
            style={fieldStyle}
          />
          <button
            data-testid="login-toggle-password"
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-4 top-1/2 -translate-y-1/2"
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeOff size={20} style={{ color: iconColor }} />
            ) : (
              <Eye size={20} style={{ color: iconColor }} />
            )}
          </button>
        </div>

        {/* Remember me */}
        <label
          data-testid="login-remember-label"
          className="w-full flex items-center gap-2 cursor-pointer select-none"
        >
          <input
            data-testid="login-remember-checkbox"
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300"
            style={{ accentColor: "#329937" }}
          />
          <span className="text-sm" style={{ color: "#374151" }}>
            Remember me
          </span>
        </label>

        {/* Submit */}
        <button
          data-testid="login-submit-button"
          type="submit"
          disabled={loading}
          className="w-full py-2.5 rounded-md text-white font-bold text-sm sm:text-base uppercase tracking-wider transition-opacity disabled:opacity-60"
          style={{ backgroundColor: "#329937" }}
        >
          {loading ? "Logging in..." : "LOG IN"}
        </button>
      </form>
    </div>
  );
}
